import React, { useState } from 'react';
import { Col, Row, Form, Button, InputGroup } from 'react-bootstrap';
import { Typewriter } from 'react-simple-typewriter';
import '../../styles/login.scss';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser } from '../../redux/slices/authSlice';
import Logo from '../../assets/logo7.png';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
    password2: '',
    role: 'client',
    agree_to_terms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Normalize Kenyan phone numbers to +254 format
  const normalizePhoneNumber = (number) => {
    if (!number) return '';
    let cleaned = number.replace(/\D/g, ''); // remove all non-digits

    if (cleaned.startsWith('07')) cleaned = '+254' + cleaned.substring(1);
    else if (cleaned.startsWith('254')) cleaned = '+' + cleaned;
    else if (!cleaned.startsWith('+254')) cleaned = '+254' + cleaned;

    return cleaned;
  };

  const validatePhoneNumber = (number) => {
    const cleaned = normalizePhoneNumber(number);
    return /^\+254\d{9}$/.test(cleaned);
  };

  const getPasswordStrength = (password) => {
    const hasEight = password.length >= 8;
    const hasNumbers = /\d/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (hasEight && hasNumbers && hasUppercase && hasSpecialCharacter)
      return 'strong';
    if (hasEight && (hasNumbers || hasUppercase || hasSpecialCharacter))
      return 'medium';
    return 'weak';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { password, password2, phone_number, agree_to_terms } = formData;

    if (!agree_to_terms) {
      toast.error('You must accept the Terms and Conditions.');
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phone_number);

    if (!validatePhoneNumber(normalizedPhone)) {
      toast.error(
        'Please enter a valid Kenyan phone number (e.g., 0700000000 or +254700000000).',
      );
      return;
    }

    if (!password) {
      toast.error('Password is required.');
      return;
    }

    const passwordStrength = getPasswordStrength(password);
    const hasEight = password.length >= 8;
    const hasNumbers = /\d/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (passwordStrength === 'weak') {
      toast.error(
        <div>
          Password is too weak. It must contain:
          <ul className="list-disc pl-5 mt-1">
            {!hasEight && <li>At least 8 characters</li>}
            {!hasNumbers && <li>At least one number</li>}
            {!hasUppercase && <li>At least one uppercase letter</li>}
            {!hasSpecialCharacter && <li>At least one special character</li>}
          </ul>
        </div>,
        { autoClose: 10000 },
      );
      return;
    }

    if (password !== password2) {
      toast.error('Passwords do not match.');
      return;
    }

    dispatch(registerUser({ ...formData, phone_number: normalizedPhone }))
      .unwrap()
      .then(() => {
        toast.success('Registration successful! Please login.');
        setTimeout(() => navigate('/login'), 2000);
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const isFormValid =
    formData.agree_to_terms &&
    validatePhoneNumber(formData.phone_number) &&
    getPasswordStrength(formData.password) !== 'weak';

  return (
    <Row className="login-row">
      <Col md={6} className="login-left">
        <div className="overlay"></div>
        <div className="login-left-content">
          <Link to="/">
            <img src={Logo} alt="Artiza Logo" className="logo" />
          </Link>
          <h2 className="typing-text">
            <Typewriter
              words={[
                'Discover amazing art.',
                'Connect with artists worldwide.',
                'Join our creative community.',
              ]}
              loop
              cursor
              cursorStyle="_"
              typeSpeed={70}
              deleteSpeed={50}
              delaySpeed={1500}
            />
          </h2>
        </div>
      </Col>

      <Col md={6} className="login-right">
        <div className="login-form-container">
          <h2>Create Account</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4" controlId="formFullName">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="full_name"
                placeholder="Enter your full name"
                className="form-input"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="formEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Enter email"
                className="form-input"
                value={formData.email.toLocaleLowerCase()}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="formPhoneNumber">
              <Form.Label>Phone Number</Form.Label>
              <PhoneInput
                country="ke"
                value={formData.phone_number}
                onChange={(phone) =>
                  setFormData({ ...formData, phone_number: phone })
                }
                inputStyle={{ width: '100%', height: '45px' }}
                buttonStyle={{ border: 'none' }}
                containerStyle={{ width: '100%' }}
                countryCodeEditable={false}
                disableDropdown={true}
                enableLongNumbers={true}
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password2"
                  placeholder="Re-enter password"
                  className="form-input"
                  value={formData.password2}
                  onChange={handleChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formTerms">
              <Form.Check
                type="checkbox"
                name="agree_to_terms"
                label={
                  <span>
                    I agree to the{' '}
                    <Link to="/terms-and-conditions" target="_blank">
                      Terms and Conditions
                    </Link>
                  </span>
                }
                checked={formData.agree_to_terms}
                onChange={handleChange}
              />
            </Form.Group>

            <Button type="submit" className="w-100 login-btn mb-3">
              {loading ? 'Registering...' : 'Register'}
            </Button>

            <div className="text-center">
              <span>Already have an account? </span>
              <Link to="/login" className="create-account-link">
                Login
              </Link>
            </div>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default Register;
